import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, interval, map, Observable, Subject, takeUntil } from 'rxjs';
import { ApiService } from '../services/api.service';
import { PaymentService } from '../services/payment.service';

type SetupAccount = {
  oid?: string;
  error?: string;
  pa_error?: string;
  o_error?: string;
  g_error?: string;
  pp_error?: string;
  expires_at_cds?: string;
  expires_at?: string;
  pa_expires_at?: string;
  o_expires_at?: string;
  g_expires_at?: string;
  pp_expires_at?: string;
  [key: string]: any;
};

type ScanStat = {
  start?: string;
  end?: string;
  ended?: boolean;
  [key: string]: any;
};

type SetupLicense = {
  workspace?: string;
  [key: string]: any;
};

@Component({
    selector: 'app-setup',
    templateUrl: './setup.component.html',
    //styleUrl: './setup.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SetupComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>(); // Destroy notifier
  public authUrl = '/api/auth';

  // private subscription = new Subscription();
  public licenses$: Observable<any[]>;
  public license$ = new BehaviorSubject<SetupLicense | null>(null);
  public user$ = new BehaviorSubject<any>(null);
  scans$ = new BehaviorSubject<ScanStat[]>([]);
  scansTimespan$!: Observable<any>;
  public accounts$ = new BehaviorSubject<SetupAccount[]>([]);
  // public monitored$ = new BehaviorSubject([]);

  // public flowChartData$ = new BehaviorSubject([]);
  // public runs$: BehaviorSubject<FlowRun[]> = new BehaviorSubject([]);
  // public runChartData$ = new BehaviorSubject([]);

  // public periodStart$ = new BehaviorSubject<Date>(null);
  // public periodEnd$ = new BehaviorSubject<Date>(null);

  public rules$ = new BehaviorSubject<any[]>([]);

  rules: any[] = [];
  cds_default = '';
  errors: SetupAccount[] = [];
  cds: SetupAccount[] = [];
  cds_errors: SetupAccount[] = [];
  pp: any[] = [];
  scope_flows: SetupAccount[] = [];
  scope_flows_errors: SetupAccount[] = [];
  scope_apps: SetupAccount[] = [];
  scope_apps_errors: SetupAccount[] = [];
  scope_events: SetupAccount[] = [];
  scope_events_errors: SetupAccount[] = [];

  scope_graph: SetupAccount[] = [];
  scope_graph_errors: SetupAccount[] = [];
  scope_pp: SetupAccount[] = [];
  scope_pp_errors: SetupAccount[] = [];

  // show_f = false;
  // show_p = false;
  // show_o = false;
  // show_c = false;

  public needConnect = false;

  constructor(private monitorService: ApiService,
    private paymentService: PaymentService
  ) {
    this.licenses$ = this.paymentService.licenses$.pipe(map((licenses)=>licenses.filter((license)=>license?.['license_teams'] == "teams")));
  }

  ngOnInit(): void {

    this.authUrl = '/api/auth';

    this.licenses$.pipe(takeUntil(this.destroy$)).subscribe((licenses)=>{
      const [license] = licenses;
      if (license) {          
        this.license$.next(license);
      }
    });

    // this.subscription.add(
    //   this.msalHelperService.user$.subscribe((user)=>{
    //     if (user) {
    //       this.user$.next(user);
    //     }
    //   })
    // );

    //this.monitorService.user$.subscribe(this.user$)

    this.monitorService.queryConfigurations().then(result => {

      const configs = result.data;
      this.accounts$.next(configs.accounts);
      this.rules$.next(configs.rules);

      this.cds = configs.accounts.filter((a: SetupAccount) => a.expires_at_cds);
      
      this.scope_flows = configs.accounts.filter((a: SetupAccount) => a.expires_at);
      this.scope_flows_errors = this.scope_flows.filter((a: SetupAccount) => !!a.error);
      this.scope_apps = configs.accounts.filter((a: SetupAccount) => a.pa_expires_at);
      this.scope_apps_errors = this.scope_apps.filter((a: SetupAccount) => !!a.pa_error);

      this.scope_events = configs.accounts.filter((a: SetupAccount) => a.o_expires_at);
      this.scope_events_errors = this.scope_apps.filter((a: SetupAccount) => !!a.o_error);

      this.scope_graph = configs.accounts.filter((a: SetupAccount) => a.g_expires_at);
      this.scope_graph_errors = configs.accounts.filter((a: SetupAccount) => !!a.g_error);    

      this.scope_pp = configs.accounts.filter((a: SetupAccount) => a.pp_expires_at);
      this.scope_pp_errors = configs.accounts.filter((a: SetupAccount) => !!a.pp_error);    

      this.pp = configs.pp;
      this.errors = configs.accounts.filter((a: SetupAccount) => !!a.error);
      if (configs.accounts.length == 0 /*|| this.errors.length > 0*/) {
        this.needConnect = true;
      }


      console.log(configs);
    });

    this.scansTimespan$ = combineLatest([interval(1000), this.scans$]).pipe(map(([t, scans]) => {
      if (!scans) {
        return ``;
      }
      return scans.map((stats: ScanStat) => {
        try {
          const start = new Date(stats.start || 0).valueOf();
          const end = stats.ended ? new Date(stats.end || 0).valueOf() : new Date().valueOf();

          const diffInMs = Math.abs(end - start);
          let diffInMins = Math.floor(diffInMs / 1000 / 60);
          const diffInSecs = Math.floor((diffInMs / 1000) % 60);

          return `${diffInMins} minutes and ${diffInSecs} seconds`;
        }
        catch (error) {
          return `... minutes and ... seconds`;
        }
      })
    }));
    this.refreshStats();
    this.scansTimespan$.pipe(takeUntil(this.destroy$)).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public refreshStats() {
    this.monitorService.queryScans().then((scans) => {
      this.scans$.next(scans || []);

      if (!scans) {
        // query again
        setTimeout(() => {
          this.refreshStats();
        }, 30000);
      }
    });
  }
  public findAccount(oid: string) {
    return this.accounts$.value.find((a) => a.oid == oid);
  }

  public encodeState(state: string, cds: string | null = null): string {
    const workspace = this.license$.getValue()?.workspace;
    // const {PartitionKey} = this.user$.getValue() || {};

    let o = {
      "s": state,
      "ws": workspace,
      cds,
      "r": `${window.location.href}`
    };

    return btoa(JSON.stringify(o));
  }

  // toggle(d:("f"|"p"|"c"|"o")) {
  //   switch(d){
  //     case "c":
  //       this.show_c = !this.show_c;
  //       break;
  //     case "f":
  //       this.show_f = !this.show_f;
  //       break;
  //     case "o":
  //       this.show_o = !this.show_o;
  //       break;
  //     case "p":
  //       this.show_p = !this.show_p;
  //       break;
  //   }
  //   return true;
  // }


}
